//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import * as yup from 'yup'
import { InferType } from 'yup'
import { ERC20Wei, ERC20WeiSigned } from './conversions'
import { yupErc20WeiRequired, yupErc20WeiSigned } from './Enums'

import { ReportingColumnVisibilityEnum, StringTypeEnum, toFieldMetaData } from './SchemaTypes'

import {
  PoolBaseAccountCTO,
  PoolBaseAccountCTOSchemaValues,
  PoolBaseAccountSummaryCTOSchemaValues,
} from './PoolBaseAccountCTO'

export const PoolLytAccountOnlyCTOSchemaValues = {
  maxWithdrawRequest: yupErc20WeiRequired
    .label('Max Withdrawal Request')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'maxWithdrawRequest',
        description: 'The maximum amount of assets you can request to be withdrawn.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  maxRedeemRequest: yupErc20WeiRequired
    .label('Max Tokens Redeemable')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'maxRedeemRequest',
        description:
          'The maximum amount that can be requested for withdrawal by the account, denominated in Vault Tokens. ',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),

  requestedSharesOf: yupErc20WeiRequired
    .label('Outstanding Withdrawal Requests (Tokens)')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'requestedSharesOf',
        description: 'The amount of outstanding withdrawal requests for the account, denominated in Vault Tokens. ',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),
  requestedAssetsOf: yupErc20WeiRequired
    .label('Outstanding Withdrawal Requests (Assets)')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'requestedAssetsOf',
        description: 'The amount of withdrawal request currently pending repayment.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  acceptedShares: yupErc20WeiRequired
    .label('Outstanding Withdrawal Accepted (Tokens)')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'acceptedShares',
        description: 'The amount of outstanding withdrawal requests for the account, denominated in Vault Tokens. ',
        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),
  acceptedAssets: yupErc20WeiRequired
    .label('Outstanding Withdrawal Accepted (Assets)')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'acceptedAssets',
        description: 'The amount of withdrawal requests that have been accepted but not yet repaid.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  principalEarningInterest: yupErc20WeiRequired
    .label('Principal Earning Interest')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'principalEarningInterest',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 10,
        description: 'Your current amount of loan principal earning the Interest Rate.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  assetsDeposited: yupErc20WeiRequired
    .label('Assets Deposited')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'assetsDeposited',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 13,
        description: 'The amount of assets you have deposited to date.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  assetsWithdrawn: yupErc20WeiRequired
    .label('Assets Withdrawn')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'assetsWithdrawn',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 14,
        description: 'The amount of your loans that have been repaid to date.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  gainLoss: yupErc20WeiSigned
    .label('InterestGain / Loss')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'gainLoss',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 11,
        description: 'The total gain, loss, or interest accrued for the account to date. Includes debits and credits.',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  gainLossInDay: yupErc20WeiSigned
    .label('Daily Interest Gain / Loss')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'gainLossInDay',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 12,
        description:
          'The total gain, loss, or interest accrued for the account for the day. Includes debits and credits.  ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  debits: yupErc20WeiRequired
    .label('Debits')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'debits',
        description: 'The total amount of debits for the account to date. Tokens are burned for debits.  ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  debitsInDay: yupErc20WeiRequired
    .label('Daily Debits')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'debitsInDay',
        description: 'The amount of debits for the account for the day. ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  credits: yupErc20WeiRequired
    .label('Credits')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'credits',
        description: 'The total amount of credits for the account to date. Tokens are minted for credits.  ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  creditsInDay: yupErc20WeiRequired
    .label('Daily Credits')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'creditsInDay',
        description: 'The amount of credits for the account for the day.  ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  fees: yupErc20WeiRequired
    .label('Fees')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'fees',
        description: ' The total amount of transaction fees paid by the account to date. ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  feesInDay: yupErc20WeiRequired
    .label('Daily Fees')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'feesInDay',
        description: ' The amount of fees paid for the day',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
  assetBalance: yupErc20WeiRequired
    .label('Asset Balance')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'assetBalance',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 10,
        description: ' The Asset Balance of the account ',
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),
}

export const PoolLytAccountSummaryCTOSchema = yup
  .object({
    ...PoolBaseAccountSummaryCTOSchemaValues,
  })
  .label('PoolLytAccountSummaryCTO')
  .meta(
    toFieldMetaData({
      propName: 'PoolLytAccountSummaryCTO',
      map: 'PoolLytAccountCTO',
      description: 'Pool Lyt Account CTO',
    })
  )
export const PoolLytAccountCTOSchema = yup
  .object({
    ...PoolBaseAccountCTOSchemaValues,
    ...PoolLytAccountOnlyCTOSchemaValues,
  })
  .label('PoolLytAccountCTO')
  .meta(toFieldMetaData({ propName: 'PoolLytAccountCTO', description: 'Pool Account CTO' }))

export const PoolLytAccountOnlyCTOSchema = yup.object({
  ...PoolLytAccountOnlyCTOSchemaValues,
})

export interface PoolLytAccountSummaryCTO extends InferType<typeof PoolLytAccountSummaryCTOSchema> {}

export interface PoolLytAccountCTO extends PoolBaseAccountCTO {
  maxWithdrawRequest: ERC20Wei
  maxRedeemRequest: ERC20Wei
  requestedAssetsOf: ERC20Wei
  requestedSharesOf: ERC20Wei
  assetBalance: ERC20Wei
  acceptedShares: ERC20Wei
  acceptedAssets: ERC20Wei
  assetsDeposited: ERC20Wei
  assetsWithdrawn: ERC20Wei

  gainLoss: ERC20WeiSigned
  gainLossInDay: ERC20WeiSigned
  debits: ERC20Wei
  debitsInDay: ERC20Wei
  credits: ERC20Wei
  creditsInDay: ERC20Wei
  fees: ERC20Wei
  feesInDay: ERC20Wei
}

type PoolLytAccountCTOInfer = InferType<typeof PoolLytAccountCTOSchema>
export const _testAPoolLytAccount: PoolLytAccountCTO = {} as unknown as PoolLytAccountCTOInfer
export const _testBPoolLytAccount: PoolLytAccountCTOInfer = {} as unknown as PoolLytAccountCTO
